/* eslint-disable no-restricted-properties */
/**
 * Is the passed in amount a valid
 * currency decimal
 * @param  {Number/String} amount The amount to validate
 * @return {Boolean}
 */
const decimalIsValid = amount => {
  const split = String(amount).split('.');
  let result = true;
  if (split.length === 2) {
    result = split[1].length <= 2;
  } else if (split.length > 2) {
    result = false;
  }
  return result;
};

const Currency = {
  /**
   * Parses a numeric value from a currency string.
   * @example
   * // returns 20.0
   * Currency.parse('$20.00');
   * @param {String|Number} currency - The formatted curremcy amount
   * @returns {Number} Returns the passed in value as a float (or 0 if NaN)
   */
  parse(currency) {
    let result = currency;
    if (currency === undefined || currency === null) {
      result = 0;
    }
    // eslint-disable-next-line no-useless-escape
    result = parseFloat(result.toString().replace(/[^\.\d]+/g, ''));
    return !Number.isNaN(result) ? result : 0;
  },

  /**
   * Format a dollar amount to a string currency value
   *
   * @param {Number|String} value - Dollar amount
   * @return {String} - Formatted string with pattern $NN.NN
   */
  format(value) {
    const numValue = Number.parseFloat(value);
    if (numValue < 0) {
      return `-$${(-1 * numValue).toFixed(2)}`;
    }
    return `$${numValue.toFixed(2)}`;
  },

  /**
   * Format a dollar amount to a shortened string currency value
   *
   * @param {Number | String} value - Dollar amount
   * @return {String} - Formatted string with pattern $NN[.NN] (.00 gets stripped)
   */
  shortFormat: value => Currency.format(value).replace(/\.00$/, ''),

  /**
   * The the passed in amount a valid tip
   * @param  {Number/String}  tipAmount The tip amount
   * @return {Boolean}
   */
  isValidTip(tipAmount) {
    const tipMax = 100000;
    const isValid = (
      tipAmount !== ''
      && Number.isFinite(Number(tipAmount))
      && tipAmount < tipMax
      && decimalIsValid(tipAmount)
    );
    return isValid;
  },

  /**
   * Format a dollar amount to a positive/negative string currency value
   *
   * Negative values are prefixed with a minus sign.
   *
   * @param {Number} value - Dollar amount
   * @return {String} -$100 or $100
   */
  formatAmount(value) {
    const floatNumber = Number.parseFloat(value).toFixed(2);
    if (floatNumber < 0) {
      const absoluteValue = Math.abs(value);
      return `-$${Number.parseFloat(absoluteValue).toFixed(2)}`;
    }
    return `$${floatNumber}`;
  },

  /**
   * Format a dollar amount to a positive/negative string currency value.
   *
   * Negative values are surrounded by parentheses.
   *
   * @param {Number} value - Dollar amount
   * @return {String} ($1,000.00) or $1,000.00
   */
  formatLedgerAmount(value) {
    const formatted = this.delimitWithCommas(Number.parseFloat(value).toFixed(2));
    if (value < 0) {
      return `($${formatted.substring(1)})`;
    }

    return `$${formatted}`;
  },

  /**
   * Insert commas between every three digits of the whole number part of a number.
   *
   * @param x
   * @returns {string}
   */
  delimitWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  /**
   * Converts numbers to K, M, B format
   * @param value - number to format
   * @returns string
   * @example convertToKMB(10100) 10.1K
   */
  convertToKMB(value) {
    const abbrev = 'KMB';
    const {
      pow, floor, abs, log,
    } = Math;
    const round = (n, precision) => {
      const prec = pow(10, precision);
      return Math.round(n * prec) / prec;
    };

    let base = floor(log(abs(value)) / log(1000));
    const suffix = abbrev[Math.min(2, base - 1)];
    base = abbrev.indexOf(suffix) + 1;
    return suffix ? round(value / pow(1000, base), 2) + suffix : `${value}`;
  },
};

export default Currency;
